import { useEffect, useState } from 'react'
import { api, type Branch as BranchRecord } from '../lib/api.ts'
import { BranchVerificationSwitch } from '../components/BranchVerificationSwitch.tsx'
import { Button } from '../components/Button.tsx'
import { Checkbox } from '../components/Checkbox.tsx'
import { EntityForm, Field, FieldGroup } from '../components/EntityForm.tsx'
import { icons } from '../components/icons.ts'
import { useToast } from '../components/Toast.tsx'
import { CAPABILITIES } from '../lib/capabilities.ts'

/**
 * One branch.
 *
 * What the ERP owns -- the code, the name, the city -- is shown as a disabled
 * field. What is ours is editable: what the branch is equipped to make, which
 * the branch rules read, since a preparation must be compounded somewhere
 * that can make it. Nothing declared means unconstrained: a single-branch
 * pharmacy leaves it empty and the branch rules stay quiet without anybody
 * switching them off.
 *
 * Whether we verify the branch at all (#676) has a card of its own: it acts
 * when switched, after a question, rather than with the save.
 */
export function Branch({ id, onBack }: { id: string; onBack: () => void }) {
  const [branch, setBranch] = useState<BranchRecord | undefined>()
  const [draft, setDraft] = useState<string[]>([])
  const [error, setError] = useState<string | undefined>()
  const [saving, setSaving] = useState(false)
  const toast = useToast()

  useEffect(() => {
    void api
      .branch(id)
      .then((r) => {
        setBranch(r.branch)
        setDraft(r.branch.capabilities)
      })
      .catch((cause: unknown) =>
        setError(cause instanceof Error ? cause.message : 'falha ao carregar'),
      )
  }, [id])

  const dirty =
    branch !== undefined &&
    (draft.length !== branch.capabilities.length ||
      draft.some((one) => !branch.capabilities.includes(one)))

  async function save() {
    if (branch === undefined) return
    setSaving(true)
    try {
      const r = await api.setBranchCapabilities(branch.id, draft)
      setBranch(r.branch)
      setDraft(r.branch.capabilities)
      toast.show('Filial salva', { tone: 'success' })
    } catch (cause) {
      toast.show(cause instanceof Error ? cause.message : 'falha ao salvar', { tone: 'error' })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="flex flex-col gap-5 p-5 pt-0">
      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant="tertiary"
          tone="neutral"
          size="small"
          leadingIcon={icons.back}
          onClick={onBack}
        >
          Filiais
        </Button>
        <h1 className="text-tH4 text-text-strong">
          {branch === undefined ? 'Filial' : `${branch.erpCode} · ${branch.name}`}
        </h1>
      </div>

      {error !== undefined && <p className="text-tP text-text-error">{error}</p>}

      {branch === undefined ? (
        error === undefined && <p className="text-tP text-text-weak">Carregando…</p>
      ) : (
        <EntityForm
          description="O que vem do sistema da farmácia aparece desabilitado. O que a filial manipula é nosso, e se edita aqui."
          onSave={() => void save()}
          saving={saving}
          dirty={dirty}
        >
          <Field label="Conta" value={branch.accountName} />
          <Field label="Código" value={branch.erpCode} />
          <Field label="Nome" value={branch.name} />
          <Field
            label="Cidade"
            value={
              branch.city === undefined
                ? ''
                : `${branch.city}${branch.state === undefined ? '' : ` / ${branch.state}`}`
            }
          />
          <Field label="Produtos" value={branch.products.toLocaleString('pt-BR')} />
          <Field
            label="Sincronizada"
            value={
              branch.syncedAt === undefined
                ? 'nunca'
                : new Date(branch.syncedAt).toLocaleString('pt-BR', {
                    dateStyle: 'short',
                    timeStyle: 'short',
                  })
            }
          />
          <FieldGroup
            label="Manipula"
            helperText="As regras de filial mandam uma forma para onde ela pode ser feita. Deixe tudo em branco se esta filial faz de tudo — as regras ficam quietas sozinhas."
          >
            <div className="flex flex-wrap gap-x-6 gap-y-2">
              {CAPABILITIES.map((capability) => (
                <label key={capability.id} className="flex items-center gap-2">
                  <Checkbox
                    checked={draft.includes(capability.id)}
                    disabled={saving}
                    onChange={(e) =>
                      setDraft((current) =>
                        e.target.checked
                          ? [...current, capability.id]
                          : current.filter((one) => one !== capability.id),
                      )
                    }
                  />
                  <span className="text-tP text-text-strong">{capability.label}</span>
                </label>
              ))}
            </div>
          </FieldGroup>
        </EntityForm>
      )}

      {branch !== undefined && (
        <EntityForm
          title="Conferência"
          description="Desligue as filiais que não são farmácia, como depósito, entregador e lançamentos de perda: as requisições delas deixam de ser lidas e conferidas, e não gastam modelo. Vale a partir da próxima leitura."
        >
          <div className="md:col-span-2">
            <BranchVerificationSwitch branch={branch} onSwitched={setBranch} />
          </div>
        </EntityForm>
      )}
    </div>
  )
}
